/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: SystemGeneralPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the System General module.
----------------------------------------------------------------*/


import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { Settings, Globe, Voicemail, Music } from 'lucide-react';
import { DEFAULT_VOICE } from '../../data/formOptions';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import FixedActionBar from '../../components/ui/FixedActionBar';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ModuleFormHeader } from '../../components/layouts';
import { Label } from '../../components/ui/label';
import { FormBox } from '../../components/ui/FormBox';
import { useSystemGeneralData } from './hooks';
import { SystemGeneralSettings } from './types/systemGeneral.types';
import { FieldWithReset } from './components/FieldWithReset';
import { SystemGeneralTabGeneral } from './components/SystemGeneralTabGeneral';
import { SystemGeneralTabVoicemail } from './components/SystemGeneralTabVoicemail';
import { SystemGeneralTabCodecs } from './components/SystemGeneralTabCodecs';

export default function SystemGeneralPage() {
    const { t } = useI18n();
    const { showSuccess, showError, showInfo } = useNotifications();
    const { selectedDomain } = useContextStore();

    const [activeTab, setActiveTab] = useState<'general' | 'voicemail' | 'codecs'>('general');

    const { data: hookData, isLoading, isSaving, initialSettings, saveSettings, deriveDefaultVoice, cloneSettings } =
        useSystemGeneralData({
            domainId: selectedDomain?.id,
            onSuccess: () => { },
            onError: () => showError(t('systemGeneral.messages.loadError')),
        });

    const [formData, setFormData] = useState<SystemGeneralSettings>(initialSettings);
    const [originalData, setOriginalData] = useState<SystemGeneralSettings>(initialSettings);
    const [originalVoice, setOriginalVoice] = useState(DEFAULT_VOICE);
    const previousHookDataRef = useRef<string | null>(null);

    useEffect(() => {
        if (hookData) {
            const currentDataString = JSON.stringify(hookData);
            if (previousHookDataRef.current !== currentDataString) {
                previousHookDataRef.current = currentDataString;
                const cloned = cloneSettings(hookData);
                setFormData(cloned);
                setOriginalData(cloned);
                setOriginalVoice(cloned.defaultVoice || DEFAULT_VOICE);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hookData]);

    const hasChanges = useMemo(() =>
        JSON.stringify(formData) !== JSON.stringify(originalData) ||
        (formData.defaultVoice || DEFAULT_VOICE) !== originalVoice,
        [formData, originalData, originalVoice]
    );

    const handleChange = useCallback((field: keyof SystemGeneralSettings, value: SystemGeneralSettings[keyof SystemGeneralSettings]) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleSave = useCallback(async () => {
        try {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { defaultVoice, defaultValues, ...payload } = formData;
            await saveSettings(payload);
            showSuccess(t('systemGeneral.messages.saveSuccess'));
            const cloned = cloneSettings(formData);
            setOriginalData(cloned);
            setOriginalVoice(formData.defaultVoice || DEFAULT_VOICE);
        } catch (error: unknown) {
            showError((error as Error)?.message || t('systemGeneral.messages.saveError'));
        }
    }, [cloneSettings, formData, saveSettings, showError, showSuccess, t]);

    const handleReset = useCallback(() => {
        const cloned = cloneSettings(originalData);
        setFormData(cloned);
        setOriginalVoice(cloned.defaultVoice || DEFAULT_VOICE);
        showInfo(t('systemGeneral.messages.resetInfo'));
    }, [cloneSettings, originalData, showInfo, t]);

    const handleResetFieldToDefault = useCallback((field: keyof SystemGeneralSettings) => {
        if (!formData.defaultValues) { showError(t('systemGeneral.messages.noDefaultValues')); return; }
        const defaultValue = formData.defaultValues[field as string];
        if (defaultValue !== undefined) {
            if (field === 'defaultLanguage') {
                const derivedVoice = deriveDefaultVoice(String(defaultValue));
                setFormData(prev => ({ ...prev, defaultLanguage: String(defaultValue), defaultVoice: derivedVoice }));
            } else {
                setFormData(prev => ({ ...prev, [field]: defaultValue }));
            }
            showInfo(t('systemGeneral.messages.fieldResetToDefault'));
        }
    }, [deriveDefaultVoice, formData.defaultValues, showError, showInfo, t]);

    const isModifiedFromDefault = useCallback((field: keyof SystemGeneralSettings): boolean => {
        if (!formData.defaultValues || field === 'defaultValues' || field === 'id' || field === 'domainId') return false;
        return JSON.stringify(formData[field]) !== JSON.stringify(formData.defaultValues[field as string]);
    }, [formData]);

    // Shared renderField factory (passed to all tab components)
    const renderField = useCallback(
        (field: keyof SystemGeneralSettings, control: ReactNode, options?: { onReset?: () => void }): ReactNode => (
            <>
                <SimpleTooltip content={t(`systemGeneral.tooltips.${String(field)}`)}>
                    <Label>{t(`systemGeneral.fields.${String(field)}`)}</Label>
                </SimpleTooltip>
                <FieldWithReset
                    isModified={isModifiedFromDefault(field)}
                    onResetToDefault={options?.onReset || (() => handleResetFieldToDefault(field))}
                >
                    {control}
                </FieldWithReset>
            </>
        ),
        [handleResetFieldToDefault, isModifiedFromDefault, t]
    );

    // -- Guards -----------------------------------------------
    if (!selectedDomain) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <Settings className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-foreground mb-2">{t('systemGeneral.noDomainSelected')}</h2>
                    <p className="text-muted-foreground">{t('systemGeneral.selectDomainMessage')}</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-muted-foreground">{t('common.loading')}</div>
            </div>
        );
    }

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<Settings className="h-5 w-5 text-primary" />}
                title={t('systemGeneral.title')}
                backButtonText={t('common.exit')}
                onBack={() => window.history.back()}
                actions={[]}
            />

            <div className="mt-8">
                <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
                    <TabsList className="w-full justify-start bg-card border border-border rounded-lg p-1 mb-1">
                        <TabsTrigger value="general" icon={<Globe className="w-4 h-4" />}>{t('systemGeneral.tabs.general')}</TabsTrigger>
                        <TabsTrigger value="voicemail" icon={<Voicemail className="w-4 h-4" />}>{t('systemGeneral.tabs.voicemail')}</TabsTrigger>
                        <TabsTrigger value="codecs" icon={<Music className="w-4 h-4" />}>{t('systemGeneral.tabs.codecs')}</TabsTrigger>
                    </TabsList>

                    <TabsContent value="general">
                        <SystemGeneralTabGeneral formData={formData} renderField={renderField} onChange={handleChange} deriveDefaultVoice={deriveDefaultVoice} />
                    </TabsContent>
                    <TabsContent value="voicemail">
                        <SystemGeneralTabVoicemail formData={formData} renderField={renderField} onChange={handleChange} />
                    </TabsContent>
                    <TabsContent value="codecs">
                        <SystemGeneralTabCodecs formData={formData} renderField={renderField} onChange={handleChange} />
                    </TabsContent>
                </Tabs>
            </div>

            <FixedActionBar
                onSave={handleSave}
                onCancel={handleReset}
                saveText={isSaving ? t('common.saving') : t('common.save')}
                cancelText={t('common.cancel')}
                saveDisabled={isSaving}
                isLoading={isSaving}
            />
        </div>
    );
}
